import { type FormEvent, Suspense, lazy, useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Logo, Spinner } from "../components/Icons";
import { AnswerInput } from "../components/live/AnswerInput";
import { ApiError, live } from "../lib/api";
import { useLiveSocket, type LiveMessage } from "../lib/live";
import type { ParticipantState } from "../lib/types";

// jsQR is ~130KB. A phone on conference wifi that just types the six
// digits should never pay for it, so it loads on tap.
const QrScanner = lazy(() =>
  import("../components/live/QrScanner").then((m) => ({ default: m.QrScanner })),
);

const TOKEN_KEY = "ripple-participant";
const REACTIONS = ["❤️", "👏", "😂", "😮", "🌊"];

export interface Saved {
  token: string;
  name: string;
  code: string;
}

function loadSaved(): Saved | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    return raw ? (JSON.parse(raw) as Saved) : null;
  } catch {
    return null;
  }
}

function forgetSaved(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Private mode or blocked storage — nothing was saved to begin with.
  }
}

/** Whether the token we're holding belongs to a different session than the
 *  one this visit is asking for.
 *
 *  Takes the query string rather than reading `location` itself, so it is a
 *  plain function with no DOM behind it. The caller passes
 *  `window.location.search` directly rather than going through the router,
 *  so the very first render already knows — otherwise someone opening a
 *  link for a new session sees a flash of the previous session's screen.
 */
export function staleFor(stored: Saved | null, search: string): boolean {
  if (!stored) return false;
  const wanted = new URLSearchParams(search).get("code");
  return Boolean(wanted) && wanted !== stored.code;
}

export default function Join() {
  const [params] = useSearchParams();
  const [saved, setSaved] = useState<Saved | null>(() => {
    const stored = loadSaved();
    // A link or QR for a different session beats whatever we were holding.
    if (staleFor(stored, window.location.search)) {
      forgetSaved();
      return null;
    }
    return stored;
  });
  const [state, setState] = useState<ParticipantState | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [ended, setEnded] = useState(false);

  const onMessage = useCallback((message: LiveMessage) => {
    switch (message.t) {
      case "state":
        setState(message as unknown as ParticipantState);
        if ((message as any).session?.status === "ended") {
          forgetSaved();
          setEnded(true);
        }
        break;
      case "slide":
        setNotice(null);
        setState((current) =>
          current
            ? {
                ...current,
                slide: message.slide as ParticipantState["slide"],
                answers_used: 0,
                answers_allowed: (message.answers_allowed as number) ?? current.answers_allowed,
                session: { ...current.session, ...(message.session as object) },
              }
            : current,
        );
        break;
      case "reveal":
        setState((current) =>
          current && current.slide
            ? {
                ...current,
                session: { ...current.session, revealed: true },
                slide: {
                  ...current.slide,
                  config: { ...current.slide.config, ...(message.config as object) },
                },
              }
            : current,
        );
        break;
      case "accepted":
        setNotice(null);
        setState((current) =>
          current ? { ...current, answers_used: message.answers_used as number } : current,
        );
        break;
      case "rejected":
        setNotice(String(message.reason));
        break;
      case "ended":
        // The token dies with the session. Drop it now so the next visit
        // opens the join form instead of stranding them on this screen.
        forgetSaved();
        setEnded(true);
        break;
    }
  }, []);

  const leave = useCallback(() => {
    forgetSaved();
    setSaved(null);
    setState(null);
    setEnded(false);
    setNotice(null);
  }, []);

  // A token the server refuses is a dead token: the session finished, or
  // was deleted. Without this the screen spins on "Joining…" forever.
  const onRefused = useCallback(
    (closeCode: number) => {
      if (closeCode === 4410) {
        forgetSaved();
        setEnded(true);
        return;
      }
      leave();
      setNotice("That session is no longer available. Enter a new code to join.");
    },
    [leave],
  );

  const { status, send } = useLiveSocket(
    saved ? `/ws/join?token=${saved.token}` : null,
    onMessage,
    onRefused,
  );

  if (ended) return <ThankYou name={state?.you.name} onRejoin={leave} />;
  if (!saved)
    return (
      <JoinForm initialCode={params.get("code") ?? ""} onJoined={setSaved} notice={notice} />
    );
  if (!state) {
    return (
      <Centered>
        <Spinner size={26} />
        <p className="muted" style={{ marginTop: 14 }}>
          {status === "closed" ? "Reconnecting…" : "Joining…"}
        </p>
      </Centered>
    );
  }

  const { slide, answers_used, answers_allowed } = state;
  const answered = answers_used >= answers_allowed;

  return (
    <div style={{ minHeight: "100vh", display: "flex", justifyContent: "center" }}>
      <div style={{ width: "100%", maxWidth: 520, padding: "20px 16px 40px" }}>
        <header style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
          <Logo size={22} />
          <span style={{ fontWeight: 700 }}>Ripple</span>
          <div style={{ flex: 1 }} />
          <span className="tiny">
            {state.you.name} · {saved.code.slice(0, 3)} {saved.code.slice(3)}
          </span>
          <span
            title={status}
            style={{
              width: 8,
              height: 8,
              borderRadius: "var(--radius-pill)",
              background: status === "open" ? "var(--success)" : "var(--warning)",
            }}
          />
        </header>

        {!slide ? (
          <div className="card" style={{ textAlign: "center", padding: 40 }}>
            <h2 style={{ fontSize: 19, marginBottom: 8 }}>You're in, {state.you.name}</h2>
            <p className="muted">Hang tight — the first question appears when the host starts.</p>
          </div>
        ) : (
          <div className="card" style={{ animation: "cardIn 250ms var(--ease-default)" }}>
            <h2 style={{ fontSize: 21, marginBottom: 18, lineHeight: 1.3 }}>
              {slide.question || "Untitled question"}
            </h2>

            {answered && slide.type !== "wordcloud" ? (
              <div style={{ textAlign: "center", padding: "26px 0" }}>
                <div style={{ fontSize: 34 }}>✓</div>
                <p className="muted" style={{ marginTop: 8 }}>
                  Answer received. Watch the big screen.
                </p>
              </div>
            ) : (
              <AnswerInput
                key={slide.id}
                slide={slide}
                answersUsed={answers_used}
                answersAllowed={answers_allowed}
                disabled={status !== "open"}
                onAnswer={(value) => send({ t: "answer", slide_id: slide.id, value })}
              />
            )}

            {notice && (
              <p role="alert" style={{ marginTop: 14, color: "var(--error)", fontSize: 14 }}>
                {notice}
              </p>
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 22 }}>
          {REACTIONS.map((emoji) => (
            <button
              key={emoji}
              onClick={() => send({ t: "react", emoji })}
              aria-label={`React ${emoji}`}
              style={{
                width: 46,
                height: 46,
                fontSize: 21,
                borderRadius: "var(--radius-pill)",
                border: "0.5px solid var(--outline)",
                background: "var(--surface)",
                cursor: "pointer",
              }}
            >
              {emoji}
            </button>
          ))}
        </div>

        <p className="tiny" style={{ textAlign: "center", marginTop: 26 }}>
          The host controls the questions.{" "}
          <button className="btn btn-text" style={{ fontSize: 12 }} onClick={leave}>
            Leave
          </button>
        </p>
      </div>
    </div>
  );
}

function JoinForm({
  initialCode,
  onJoined,
  notice,
}: {
  initialCode: string;
  onJoined: (saved: Saved) => void;
  notice?: string | null;
}) {
  const [code, setCode] = useState(initialCode);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(notice ?? null);
  const [busy, setBusy] = useState(false);
  // Someone arriving with ?code= already filled has no reason to scan.
  const [scanning, setScanning] = useState(false);

  const onScanned = useCallback((found: string) => {
    setCode(found);
    setScanning(false);
    setError(null);
  }, []);

  // Arriving from a scanned QR skips straight to the name field.
  useEffect(() => setCode(initialCode), [initialCode]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await live.join(code.trim(), name.trim(), loadSaved()?.token);
      const saved: Saved = { token: result.token, name: result.name, code: code.trim() };
      localStorage.setItem(TOKEN_KEY, JSON.stringify(saved));
      onJoined(saved);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reach the session.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Centered>
      <div style={{ width: "100%", maxWidth: 380 }}>
        <div
          style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "center", marginBottom: 24 }}
        >
          <Logo size={30} />
          <span style={{ fontSize: 25, fontWeight: 700 }}>Ripple</span>
        </div>
        <form className="card" onSubmit={submit}>
          <h1 style={{ fontSize: 19, marginBottom: 4 }}>Join the session</h1>
          <p className="muted" style={{ marginBottom: 20 }}>
            Scan the code on screen, or type the six digits.
          </p>

          {scanning ? (
            <Suspense
              fallback={
                <div style={{ display: "grid", placeItems: "center", height: 120 }}>
                  <Spinner size={24} />
                </div>
              }
            >
              <QrScanner onFound={onScanned} onClose={() => setScanning(false)} />
            </Suspense>
          ) : (
            <button
              type="button"
              className="btn btn-outline"
              style={{ width: "100%", marginBottom: 16, height: 52 }}
              onClick={() => setScanning(true)}
            >
              <ScanIcon /> Scan QR code
            </button>
          )}

          <label className="label" htmlFor="code">
            Session code
          </label>
          <input
            id="code"
            className="input"
            inputMode="numeric"
            autoComplete="off"
            maxLength={6}
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            style={{ marginBottom: 16, fontSize: 22, letterSpacing: 5, textAlign: "center", height: 58 }}
          />

          <label className="label" htmlFor="name">
            Your name{" "}
            <span style={{ fontWeight: 400, color: "var(--text-tertiary)" }}>— optional</span>
          </label>
          <input
            id="name"
            className="input"
            maxLength={40}
            placeholder="Leave blank to stay anonymous"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ marginBottom: 8 }}
          />
          <p className="tiny" style={{ marginBottom: 20 }}>
            {name.trim()
              ? `The host will see your answers as "${name.trim()}".`
              : "You'll be listed as Anonymous, with a number so the host can tell people apart."}
          </p>

          {error && (
            <p role="alert" style={{ marginBottom: 14, color: "var(--error)", fontSize: 14 }}>
              {error}
            </p>
          )}

          <button className="btn" style={{ width: "100%" }} disabled={busy || code.length < 6}>
            {busy ? <Spinner /> : name.trim() ? "Join" : "Join anonymously"}
          </button>
        </form>
      </div>
    </Centered>
  );
}

function ScanIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={18}
      height={18}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 8V5.5A1.5 1.5 0 0 1 4.5 4H8" />
      <path d="M16 4h3.5A1.5 1.5 0 0 1 21 5.5V8" />
      <path d="M21 16v2.5a1.5 1.5 0 0 1-1.5 1.5H16" />
      <path d="M8 20H4.5A1.5 1.5 0 0 1 3 18.5V16" />
      <rect x="7.5" y="7.5" width="9" height="9" rx="1.5" />
    </svg>
  );
}

function ThankYou({ name, onRejoin }: { name?: string; onRejoin: () => void }) {
  return (
    <Centered>
      <div className="card" style={{ maxWidth: 400, textAlign: "center", padding: 44 }}>
        <Logo size={46} />
        <h1 style={{ fontSize: 24, margin: "18px 0 8px" }}>
          Thanks for taking part{name ? `, ${name}` : ""}!
        </h1>
        <p className="muted" style={{ marginBottom: 24 }}>
          That's the end of the session. Your answers are up on the big screen.
        </p>
        {/* Without a way out, this screen is a dead end for anyone whose
            next session starts in the same browser. */}
        <button className="btn" style={{ width: "100%" }} onClick={onRejoin}>
          Join another session
        </button>
      </div>
    </Centered>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <main
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 20,
        background:
          "radial-gradient(700px 400px at 50% -10%, rgba(95,168,189,0.16), transparent 60%), var(--bg)",
      }}
    >
      <div style={{ width: "100%", display: "grid", placeItems: "center" }}>{children}</div>
    </main>
  );
}
